import { IconButton, useTheme } from '@fluentui/react';
import { IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ICompareConfiguration } from '../CompareLookupDialog/CompareLookupDialog';
import { IMarkerSet, QUICK_NAV_OVERLAY_VISIBLE_STYLE } from './Compare.constants';
import CompareRadialNavOverlay from './CompareRadialNavOverlay';

interface ICompareFloatingActionButtonsProps {
    model: IGroupConfigurationComparisonVM | null;
    configuration: ICompareConfiguration | null;
    markers: IMarkerSet;
    onSetIsQuickNavOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CompareFloatingActionButtons: React.FC<ICompareFloatingActionButtonsProps> = ({
    model,
    configuration,
    markers,
    onSetIsQuickNavOpen,
}) => {
    const flashStyleElementId = 'compare-focus-flash-keyframes';
    const theme = useTheme();
    const [radialNavIsHidden, setRadialNavIsHidden] = useState(true);
    const [zoomLevel, setZoomLevel] = useState<85 | 100 | null>(null);
    const currentChangeIndexRef = useRef<number>(-1);
    const activeChangeElementRef = useRef<HTMLElement | null>(null);

    const getChangeElements = useCallback((): HTMLElement[] => {
        const allChangeElements = Array.from(document.querySelectorAll<HTMLElement>('.row-select-anchor, .merge-toggle-options'));

        if (allChangeElements.length === 0)
            return [];

        const visibleChangeElements = allChangeElements.filter((element) => element.getClientRects().length > 0);
        return visibleChangeElements.length > 0 ? visibleChangeElements : allChangeElements;
    }, []);

    const focusChangeElement = useCallback((elements: HTMLElement[], index: number) => {
        const element = elements[index];

        if (!element)
            return;

        if (activeChangeElementRef.current) {
            activeChangeElementRef.current.style.outline = '';
            activeChangeElementRef.current.style.boxShadow = '';
        }

        activeChangeElementRef.current = element;

        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.outline = '4px solid #f5d020';
        element.style.boxShadow = 'inset 0 0 0 2px rgba(245, 208, 32, 0.35)';
        element.style.animation = 'compareFocusFlash 900ms ease-in-out 2';

        window.setTimeout(() => {
            if (activeChangeElementRef.current === element) {
                element.style.outline = '';
                element.style.boxShadow = '';
                element.style.animation = '';
            }
        }, 2000);
    }, []);

    useEffect(() => {
        const style = document.documentElement.style as CSSStyleDeclaration & { zoom: string };
        if (zoomLevel === null) {
            style.zoom = '';
        } else {
            style.zoom = `${zoomLevel}%`;
        }
        return () => {
            style.zoom = '';
        };
    }, [zoomLevel]);

    useEffect(() => {
        if (document.getElementById(flashStyleElementId))
            return;

        const styleElement = document.createElement('style');
        styleElement.id = flashStyleElementId;
        styleElement.textContent = `
            @keyframes compareFocusFlash {
                0%, 100% {
                    outline-color: #f5d020;
                    box-shadow: inset 0 0 0 2px rgba(245, 208, 32, 0.1);
                    border-radius: 4px;
                }
                50% {
                    outline-color: #ffffff95;
                    box-shadow: inset 0 0 0 2px rgba(245, 208, 32, 0.45);
                    border-radius: 4px;

                }
            }
        `;

        document.head.appendChild(styleElement);

        return () => {
            styleElement.remove();
        };
    }, [flashStyleElementId]);

    const navigateChanges = useCallback((direction: 1 | -1) => {
        const changeElements = getChangeElements();

        if (changeElements.length === 0)
            return;

        let nextIndex = currentChangeIndexRef.current + direction;

        if (nextIndex >= changeElements.length)
            nextIndex = 0;

        if (nextIndex < 0)
            nextIndex = changeElements.length - 1;

        currentChangeIndexRef.current = nextIndex;
        focusChangeElement(changeElements, nextIndex);
    }, [focusChangeElement, getChangeElements]);

    const findNextChange = useCallback(() => {
        navigateChanges(1);
    }, [navigateChanges]);

    const findPreviousChange = useCallback(() => {
        navigateChanges(-1);
    }, [navigateChanges]);

    const cycleToggleOption = useCallback((direction: 1 | -1) => {
        const activeChangeElement = activeChangeElementRef.current;

        if (!activeChangeElement)
            return;

        const toggleOptions = Array.from(activeChangeElement.querySelectorAll<HTMLElement>('.toggle-option'));

        if (toggleOptions.length === 0)
            return;

        const currentActiveToggleIndex = toggleOptions.findIndex((toggleOption) => toggleOption.classList.contains('selected'));
        let nextToggleOptionIndex = (currentActiveToggleIndex === -1)
            ? (direction === 1 ? 0 : toggleOptions.length - 1)
            : currentActiveToggleIndex + direction;

        if (nextToggleOptionIndex >= toggleOptions.length)
            nextToggleOptionIndex = 0;

        if (nextToggleOptionIndex < 0)
            nextToggleOptionIndex = toggleOptions.length - 1;

        const targetToggleOption = toggleOptions[nextToggleOptionIndex];
        targetToggleOption.click();
        targetToggleOption.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }, []);

    useEffect(() => {
        // Reset highlight state whenever the comparison model changes.
        currentChangeIndexRef.current = -1;

        if (activeChangeElementRef.current) {
            activeChangeElementRef.current.style.outline = '';
            activeChangeElementRef.current.style.boxShadow = '';
            activeChangeElementRef.current = null;
        }
    }, [model]);

    useEffect(() => {
        // Keep body from scrolling while either overlay is open.
        document.body.style.overflow = radialNavIsHidden ? 'auto' : 'hidden';

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [radialNavIsHidden]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.shiftKey && e.ctrlKey && !e.altKey) {
                onSetIsQuickNavOpen?.((prev) => !prev);
                return;
            }

            if (e.shiftKey && e.altKey && !e.ctrlKey) {
                setRadialNavIsHidden((prev) => !prev);
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowDown') {
                e.preventDefault();
                findNextChange();
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowUp') {
                e.preventDefault();
                findPreviousChange();
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowRight') {
                e.preventDefault();
                cycleToggleOption(1);
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowLeft') {
                e.preventDefault();
                cycleToggleOption(-1);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [cycleToggleOption, findNextChange, findPreviousChange]);

    const rootBaseStyles = {
        zIndex: (QUICK_NAV_OVERLAY_VISIBLE_STYLE.zIndex as any) - 1,
        backgroundColor: theme.palette.white,
        border: '1px solid #20202020',
        borderRadius: '50px',
        position: 'fixed' as const,
        right: '10px',
        ':hover': {
            background: theme.palette.themeDark,
            color: theme.palette.white,
        },
    };

    return (
        <>
            <IconButton
                iconProps={{ iconName: zoomLevel === 85 ? 'ZoomIn' : 'ZoomOut' }}
                title={zoomLevel === 85 ? 'Zoom In to 100%' : 'Zoom Out to 85%'}
                ariaLabel="Toggle Zoom Level"
                onClick={() => setZoomLevel((prev) => (prev === 85 ? 100 : 85))}
                styles={{
                    icon: { fontSize: '14px' },
                    root: {
                        ...rootBaseStyles,
                        bottom: '170px',
                    },
                }}
            />

            <IconButton
                iconProps={{ iconName: 'RadioBtnOn' }}
                title="Quick Radial Navigation (Shift + Alt)"
                ariaLabel="Quick Radial Navigation"
                onClick={() => setRadialNavIsHidden(false)}
                styles={{
                    icon: { fontSize: '14px' },
                    root: {
                        ...rootBaseStyles,
                        bottom: '90px',
                    },
                }}
            />
            <IconButton
                iconProps={{ iconName: 'ScrollUpDown' }}
                title="Previous Change (Ctrl + Up) / Next Change (Ctrl + Down)"
                ariaLabel="Previous/Next Change"
                onClick={findNextChange}
                styles={{
                    icon: { fontSize: '14px' },
                    root: {
                        ...rootBaseStyles,
                        bottom: '130px',
                    },
                }}
            />
            <IconButton
                iconProps={{ iconName: 'DoubleBookmark' }}
                title="Quick Navigation (Ctrl + Shift)"
                ariaLabel="Quick Navigation (Ctrl + Shift)"
                onClick={() => onSetIsQuickNavOpen?.(t => !t)}
                styles={{
                    icon: { fontSize: '14px' },
                    root: {
                        ...rootBaseStyles,
                        bottom: '50px',
                    },
                }}
            />
            {configuration && model && (
                <CompareRadialNavOverlay
                    model={model}
                    configuration={configuration}
                    isHidden={radialNavIsHidden}
                    markers={markers}
                    onDismiss={() => setRadialNavIsHidden(true)}
                />
            )}

        </>
    );
};

export default CompareFloatingActionButtons;
